import { useState, useEffect } from "react";
import { Button, DatePicker, DateValue, Select, SelectItem, Textarea } from "@heroui/react";
import { getLocalTimeZone, today } from "@internationalized/date";
import { useShallow } from "zustand/shallow";
import { CheckSquareIcon, TrashSimpleIcon, FlagIcon } from "@phosphor-icons/react";

import { useBoundStore } from "@/app/store";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { ICON_SIZE } from "@/config/constants";
import { TODO_PRIORITY } from "../constants";

interface TodoModalProps {
  initialValues?: TodoFormValues;
  isOpen: boolean;
  onDelete: () => void;
  onSave: (values: TodoFormValues) => Promise<void>;
  onOpenChange: (open: boolean) => void;
}

export function ManageTodo({ initialValues, isOpen, onDelete, onOpenChange, onSave }: TodoModalProps) {
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const { formValues, handleChange, handlePriorityChange, handleDateChange, resetForm, errors, validateForm } =
    useTodoForm(initialValues);
  const now = today(getLocalTimeZone());

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      resetForm();
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formValues);
      resetForm();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && formValues.name) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      resetForm();
    }
  };

  const handleClear = () => {
    handleDateChange(null);
  };

  const getPriorityFlag = (priority: number) => {
    if (priority === TODO_PRIORITY.HIGH.value) {
      return <FlagIcon size={ICON_SIZE.sm} weight="fill" className="text-danger" />;
    } else if (priority === TODO_PRIORITY.MEDIUM.value) {
      return <FlagIcon size={ICON_SIZE.sm} weight="fill" className="text-warning" />;
    } else if (priority === TODO_PRIORITY.LOW.value) {
      return <FlagIcon size={ICON_SIZE.sm} weight="fill" className="text-success" />;
    } else {
      return <FlagIcon size={ICON_SIZE.sm} weight="fill" className="text-default-400" />;
    }
  };

  return (
    <DrawerAndModal
      id="todo-modal"
      icon={<CheckSquareIcon />}
      title={initialValues ? "Edit todo" : "New todo"}
      closeLabel="Cancel"
      confirmLabel={initialValues ? "Save" : "Add"}
      onConfirm={handleSubmit}
      externalState={{ isOpen, onOpenChange: handleOpenChange }}
    >
      <div className="space-y-4">
        <Textarea
          autoFocus={!initialValues}
          minRows={1}
          maxRows={3}
          autoComplete="off"
          errorMessage={errors.name}
          isInvalid={!!errors.name}
          name="name"
          label="Description"
          size="sm"
          value={formValues.name}
          onChange={handleChange}
          onKeyDown={handleNameKeyDown}
        />
        <DatePicker
          className="max-w-md"
          errorMessage={errors.dueDate}
          CalendarBottomContent={
            <div className="mx-6 pb-2">
              <Button fullWidth size="sm" variant="bordered" onPress={handleClear}>
                Clear
              </Button>
            </div>
          }
          granularity="day"
          isInvalid={!!errors.dueDate}
          minValue={now}
          label="Due Date"
          showMonthAndYearPickers
          size="sm"
          value={formValues.dueDate}
          onChange={handleDateChange}
        />
        <Select
          label="Priority"
          startContent={formValues.priority !== undefined ? getPriorityFlag(formValues.priority) : null}
          size="sm"
          selectedKeys={formValues.priority ? [formValues.priority.toString()] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0];
            if (selected) {
              handlePriorityChange(selected as string);
            }
          }}
          className="max-w-md"
        >
          <SelectItem
            key={TODO_PRIORITY.HIGH.value.toString()}
            textValue={TODO_PRIORITY.HIGH.label}
            startContent={getPriorityFlag(TODO_PRIORITY.HIGH.value)}
          >
            {TODO_PRIORITY.HIGH.label}
          </SelectItem>
          <SelectItem
            key={TODO_PRIORITY.MEDIUM.value.toString()}
            textValue={TODO_PRIORITY.MEDIUM.label}
            startContent={getPriorityFlag(TODO_PRIORITY.MEDIUM.value)}
          >
            {TODO_PRIORITY.MEDIUM.label}
          </SelectItem>
          <SelectItem
            key={TODO_PRIORITY.LOW.value.toString()}
            textValue={TODO_PRIORITY.LOW.label}
            startContent={getPriorityFlag(TODO_PRIORITY.LOW.value)}
          >
            {TODO_PRIORITY.LOW.label}
          </SelectItem>
          <SelectItem
            key={TODO_PRIORITY.NONE.value.toString()}
            textValue={TODO_PRIORITY.NONE.label}
            startContent={getPriorityFlag(TODO_PRIORITY.NONE.value)}
          >
            {TODO_PRIORITY.NONE.label}
          </SelectItem>
        </Select>
        {deviceType === "mobile" && (
          <div className="flex gap-2 pt-2">
            {initialValues && (
              <Button color="danger" isIconOnly variant="flat" fullWidth onPress={onDelete}>
                <TrashSimpleIcon size={ICON_SIZE.md} />
              </Button>
            )}
            <Button variant="flat" fullWidth onPress={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button color="primary" fullWidth onPress={handleSubmit}>
              Save
            </Button>
          </div>
        )}
      </div>
    </DrawerAndModal>
  );
}

export interface TodoFormValues {
  name: string;
  priority: number;
  dueDate?: DateValue | null;
}

function useTodoForm(initialValues?: TodoFormValues) {
  const [formValues, setFormValues] = useState<TodoFormValues>({
    name: initialValues?.name || "",
    priority: initialValues?.priority ?? TODO_PRIORITY.NONE.value,
    dueDate: initialValues?.dueDate || null,
  });
  const [errors, setErrors] = useState<{ name?: string; priority?: string; dueDate?: string }>({});

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        name: initialValues.name || "",
        priority: initialValues.priority ?? TODO_PRIORITY.NONE.value,
        dueDate: initialValues.dueDate || null,
      });
    } else {
      setFormValues({
        name: "",
        priority: TODO_PRIORITY.NONE.value,
        dueDate: null,
      });
    }
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePriorityChange = (value: string) => {
    setFormValues((prev) => ({ ...prev, priority: Number(value) }));
    setErrors((prev) => ({ ...prev, priority: undefined }));
  };

  const handleDateChange = (value: DateValue | null) => {
    setFormValues((prev) => ({ ...prev, dueDate: value }));
    setErrors((prev) => ({ ...prev, dueDate: undefined }));
  };

  const validateForm = () => {
    const newErrors: { name?: string; priority?: string; dueDate?: string } = {};
    const now = today(getLocalTimeZone());

    if (!formValues.name) {
      newErrors.name = "Description is required.";
    } else if (formValues.name.length > 255) {
      newErrors.name = "Description must not exceed 255 characters.";
    }

    if (formValues.dueDate && formValues.dueDate.compare(now) < 0) {
      newErrors.dueDate = "Due date cannot be in the past.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormValues({
      name: initialValues?.name || "",
      priority: initialValues?.priority ?? TODO_PRIORITY.NONE.value,
      dueDate: initialValues?.dueDate || null,
    });
    setErrors({});
  };

  return {
    formValues,
    handleChange,
    handlePriorityChange,
    handleDateChange,
    resetForm,
    errors,
    validateForm,
  };
}
